// Role IDs matching backend database
export const ROLES = {
  SUPER_ADMIN: 5,  // Súper Administrador - Acceso Total
  ADMIN: 1,        // Administrador - Acceso Total
  TECHNICIAN: 2,   // Técnico - Solo crear registros
  SUPERVISOR: 3,   // Supervisor - Ver y crear registros
  VIEWER: 4,       // Visualizador - Solo Lectura (sin uso actual)
} as const;

export type RoleId = (typeof ROLES)[keyof typeof ROLES];

// All available permissions in the system
export type Permission =
  // View permissions (routes)
  | 'view:dashboard'
  | 'view:registro'
  | 'view:scuba3s'
  | 'view:historial-alertas'
  | 'view:analisis'
  | 'view:reportes'
  | 'view:configuracion'
  | 'view:quimicos'
  | 'view:admin'
  | 'view:inventario'
  // Pool permissions
  | 'pools:read'
  | 'pools:write'
  | 'pools:delete'
  // Technician permissions
  | 'technicians:read'
  | 'technicians:write'
  | 'technicians:delete'
  // Measurement permissions
  | 'measurements:read'
  | 'measurements:write'
  // Chemical Products permissions
  | 'chemicals:read'
  | 'chemicals:write'
  | 'chemicals:delete'
  // Chemical Inventory permissions
  | 'inventory:read'
  | 'inventory:write'
  | 'inventory:usage';

// Role names for display (Spanish)
export const ROLE_NAMES: Record<RoleId, string> = {
  [ROLES.SUPER_ADMIN]: 'Super Administrador',
  [ROLES.ADMIN]: 'Administrador',
  [ROLES.TECHNICIAN]: 'Técnico',
  [ROLES.SUPERVISOR]: 'Supervisor',
  [ROLES.VIEWER]: 'Visualizador',
};

// Permissions for each role
export const ROLE_PERMISSIONS: Record<RoleId, Permission[]> = {
  // Super Admin - Full access
  [ROLES.SUPER_ADMIN]: [
    'view:dashboard',
    'view:registro',
    'view:scuba3s',
    'view:historial-alertas',
    'view:analisis',
    'view:reportes',
    'view:configuracion',
    'view:quimicos',
    'view:inventario',
    'view:admin',
    'pools:read',
    'pools:write',
    'pools:delete',
    'technicians:read',
    'technicians:write',
    'technicians:delete',
    'measurements:read',
    'measurements:write',
    'chemicals:read',
    'chemicals:write',
    'chemicals:delete',
    'inventory:read',
    'inventory:write',
    'inventory:usage',
  ],

  // Admin - Full access
  [ROLES.ADMIN]: [
    'view:dashboard',
    'view:registro',
    'view:scuba3s',
    'view:historial-alertas',
    'view:analisis',
    'view:reportes',
    'view:configuracion',
    'view:quimicos',
    'view:inventario',
    'pools:read',
    'pools:write',
    'pools:delete',
    'technicians:read',
    'technicians:write',
    'technicians:delete',
    'measurements:read',
    'measurements:write',
    'chemicals:read',
    'chemicals:write',
    'chemicals:delete',
    'inventory:read',
    'inventory:write',
    'inventory:usage',
  ],

  // Technician - ONLY create measurements
  [ROLES.TECHNICIAN]: [
    'view:registro',
    'measurements:write',
  ],

  // Supervisor - View and create measurements
  [ROLES.SUPERVISOR]: [
    'view:dashboard',
    'view:registro',
    'measurements:read',
    'measurements:write',
  ],

  // Viewer - Read only dashboard
  [ROLES.VIEWER]: [
    'view:dashboard',
    'measurements:read',
  ],
};

// Route to permission mapping
export const ROUTE_PERMISSIONS: Record<string, Permission> = {
  '/dashboard': 'view:dashboard',
  '/registro': 'view:registro',
  '/scuba3s': 'view:scuba3s',
  '/historial-alertas': 'view:historial-alertas',
  '/analisis': 'view:analisis',
  '/reportes': 'view:reportes',
  '/configuracion': 'view:configuracion',
  '/quimicos': 'view:quimicos',
  '/inventario': 'view:inventario',
  '/admin': 'view:admin',
};

// Get default redirect path for a role
export function getDefaultRedirectPath(roleId: RoleId): string {
  const permissions = ROLE_PERMISSIONS[roleId] || [];

  if (permissions.includes('view:dashboard')) {
    return '/dashboard';
  }
  if (permissions.includes('view:registro')) {
    return '/registro';
  }
  return '/';
}
